import axios from 'axios';

import { ENV } from '@/config/env';
import { getCookie, setCookie } from '@/utils';

/**
 * Cognito JWT renewal for the minisite.
 *
 * Renews expired Cognito JWTs using the refresh token via the BFF.
 * The id-token cookie outlives the JWT it carries, so renewal is
 * attempted before every authenticated request.
 */

const COGNITO_COOKIE = 'ap_cognito_authorization';
const REFRESH_COOKIE = 'cognito_refresh_token';
const REFRESH_PATH = '/auth/v1/sessions/refresh';
// Mirrors the wrapper. Shared with other apps — do not shorten here.
const COOKIE_EXPIRE_DAYS = 365;

/** Leeway so a token that expires mid-flight is never sent. */
const EXPIRY_SKEW_SECONDS = 30;

// The BFF also returns `expires_in`, unused for now.
type BffRefreshResponse = {
  data: {
    token: string;
  };
};

/** In-flight refresh, shared so N parallel requests trigger a single POST. */
let inFlightRefresh: Promise<string> | null = null;

const isBrowser = () => typeof window !== 'undefined';

/** Strips the `Bearer ` prefix the wrapper writes into the cookie. */
const stripBearer = (value: string) => value.replace(/^Bearer\s+/i, '');

/**
 * Decodes the JWT payload's `exp` without pulling in a new dependency.
 * Returns `null` when the token cannot be decoded or carries no `exp`; callers then
 * treat it as expired, since refreshing is safer than forwarding garbage.
 */
const getTokenExp = (token: string): number | null => {
  try {
    const payload = token.split('.')[1];

    if (!payload) {
      return null;
    }

    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(window.atob(normalized));

    return typeof exp === 'number' ? exp : null;
  } catch {
    return null;
  }
};

const isExpired = (token: string): boolean => {
  const exp = getTokenExp(token);

  if (exp === null) {
    return true;
  }

  return exp - EXPIRY_SKEW_SECONDS <= Math.floor(Date.now() / 1000);
};

/**
 * Asks the auth BFF for a fresh token. Contract: `{ data: { token, expires_in } }`
 * (see `agendapro_http_wrapper` · commit 3351ebdb). Bare `axios` is used on purpose:
 * this repo's instances carry interceptors that camelize responses and inject this
 * very token, which would cause recursion.
 */
const requestNewToken = async (): Promise<string> => {
  const refreshToken = getCookie(REFRESH_COOKIE);

  if (!refreshToken) {
    return '';
  }

  try {
    const { data } = await axios.post<BffRefreshResponse>(
      `${ENV.AP_API_URL}${REFRESH_PATH}`,
      { refresh_token: refreshToken },
      { timeout: 15000, headers: { 'Content-Type': 'application/json' } },
    );

    const token = data?.data?.token;

    if (!token) {
      return '';
    }

    setCookie(COGNITO_COOKIE, `Bearer ${token}`, COOKIE_EXPIRE_DAYS);

    return `Bearer ${token}`;
  } catch {
    // Do not delete the shared session cookie — other apps depend on it.
    // A failed refresh is reported by returning ''.
    return '';
  }
};

const refreshOnce = (): Promise<string> => {
  if (!inFlightRefresh) {
    inFlightRefresh = requestNewToken().finally(() => {
      inFlightRefresh = null;
    });
  }

  return inFlightRefresh;
};

/**
 * Raised when a Cognito-gated call is skipped because the session could not be
 * renewed. It exists so callers can tell "the merchant has no back-office session"
 * apart from a genuine backend failure, instead of firing a request that is a
 * guaranteed 401.
 */
export class CognitoSessionExpiredError extends Error {
  constructor() {
    super('Cognito session expired and could not be renewed');
    this.name = 'CognitoSessionExpiredError';
  }
}

/**
 * A valid `Authorization` header for endpoints that require a Cognito session.
 * Returns `''` when there is no recoverable session.
 */
export const getValidCognitoAuthHeader = async (): Promise<string> => {
  if (!isBrowser()) {
    return '';
  }

  const cookieValue = getCookie(COGNITO_COOKIE);

  if (!cookieValue) {
    // No id token but a refresh token means the session IS recoverable: that is the
    // merchant who signed in to app.agendapro.com a while ago and came back here.
    return getCookie(REFRESH_COOKIE) ? refreshOnce() : '';
  }

  if (!isExpired(stripBearer(cookieValue))) {
    return cookieValue;
  }

  return refreshOnce();
};

/** Forces a refresh ignoring `exp`. Used to retry once after a 401. */
export const forceCognitoRefresh = (): Promise<string> => (isBrowser() ? refreshOnce() : Promise.resolve(''));

/**
 * Whether there is a potentially recoverable Cognito session. Used to enable admin
 * queries: a refresh token is enough, because an expired id token gets renewed.
 */
export const hasRecoverableCognitoSession = (): boolean =>
  isBrowser() && (!!getCookie(REFRESH_COOKIE) || !!getCookie(COGNITO_COOKIE));
